import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { HotToastService } from "@ngneat/hot-toast";
import { environment } from "../../environments/environment";

interface UploadedFile {
  file: File;
  id: string;
  status: "uploading" | "success" | "error";
  progress: number;
}

@Component({
  selector: "app-w9-upload",
  standalone: true,
  imports: [],
  templateUrl: "./w9-upload.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./w9-upload.component.scss"],
})
export class W9UploadComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private toastr = inject(HotToastService);

  isDragOver = false;
  uploadedFile: UploadedFile | null = null;
  isUploading = false;
  uploadComplete = false;
  alreadyReceived = false;
  linkInactive = false;
  accessToken: string | null = null;
  vendorName = "COMPANY NAME"; // Default fallback
  isLoading = true;

  ngOnInit() {
    this.accessToken = this.route.snapshot.paramMap.get("token");
    if (!this.accessToken) {
      console.error("No access token provided in URL");
      return;
    }

    this.loadVendorInfo();
  }

  loadVendorInfo() {
    if (!this.accessToken) return;

    this.http
      .get(`${environment.apiUrl}/integration/common/w9-upload/`, {
        params: { access_token: this.accessToken },
      })
      .subscribe({
        next: (response: any) => {
          this.vendorName = response.vendor_name || "COMPANY NAME";
          this.isLoading = false;

          const status = response.status;
          if (["submitted", "approved", "reviewed"].includes(status)) {
            // W-9 already on file: show the closed/success state instead of
            // an upload form that the backend would reject.
            this.uploadComplete = true;
            this.alreadyReceived = true;
          } else if (status !== "requested") {
            // rejected / not_requested / unknown: this link cannot accept
            // uploads anymore.
            this.linkInactive = true;
          }
        },
        error: (error) => {
          console.error("Error loading vendor info:", error);
          this.vendorName = "COMPANY NAME"; // Keep fallback
          this.isLoading = false;
        },
      });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.uploadComplete) return;
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    if (this.uploadComplete) return;
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;

    if (this.uploadComplete) return;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.uploadComplete) return;

    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  handleFile(file: File) {
    // Validate file type
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const isValidType =
      validTypes.includes(file.type) ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isValidType) {
      this.showErrorMessage("Please upload only PDF, JPG, PNG, or DOC files.");
      return;
    }

    // Validate file size (e.g., max 10MB)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      this.showErrorMessage("File size must be less than 10MB.");
      return;
    }

    this.uploadedFile = {
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: "uploading",
      progress: 0,
    };

    this.uploadFile();
  }

  uploadFile() {
    if (!this.uploadedFile || !this.accessToken) {
      console.error("Missing uploaded file or access token");
      return;
    }

    this.isUploading = true;

    const formData = new FormData();
    formData.append("w9_file", this.uploadedFile.file);
    formData.append("access_token", this.accessToken);

    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      if (this.uploadedFile && this.uploadedFile.progress < 90) {
        this.uploadedFile.progress += Math.random() * 20;
      }
    }, 200);

    // Make actual API call to upload the file
    this.http
      .post(`${environment.apiUrl}/integration/common/w9-upload/`, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe({
        next: (event: any) => {
          if (event.type === 4) {
            // HttpEventType.Response
            clearInterval(progressInterval);
            if (this.uploadedFile) {
              this.uploadedFile.progress = 100;
              this.uploadedFile.status = "success";
            }
            this.isUploading = false;
            this.uploadComplete = true;
          }
        },
        error: (error) => {
          console.error("Upload failed:", error);
          clearInterval(progressInterval);
          if (this.uploadedFile) {
            this.uploadedFile.status = "error";
          }
          this.isUploading = false;

          // Show specific error message if it's an AI validation error
          let errorMessage = "Upload failed. Please try again.";
          if (error.error && error.error.error) {
            errorMessage = error.error.error;

            // If there's an AI reason, include it
            if (error.error.ai_reason) {
              errorMessage += `\n\nAI Analysis: ${error.error.ai_reason}`;
            }
          }

          this.showErrorMessage(errorMessage);
        },
      });
  }

  showErrorMessage(message: string) {
    this.toastr.error(message, {
      duration: 0, // Sticky toast (duration: 0 means it won't auto-dismiss)
      dismissible: true, // Allow manual dismissal
    });
  }

  removeFile() {
    if (!this.uploadComplete) {
      this.uploadedFile = null;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
}
